import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {vec3, quat} from 'gl-matrix';

/**
 * particle-system
 */
export class ParticleSystem extends Component {
    static TypeName = 'particle-system';

    @property.object()
    particlePrefab!: Object3D;

    @property.float(40)
    cacheSize!: number; // Total number of particles in the cache

    @property.float(4)
    spawnLimit!: number; // Number of particles per spawn

    @property.float(2)
    particleLifetime!: number; // Lifetime of each particle (seconds)

    @property.enum(['firework', 'fountain'], 0)
    animationType!: Enumerator;

    private _particleCache: Object3D[] = [];
    private _particlesInUse: {
        particle: Object3D;
        velocity: vec3;
        rotationSpeed: vec3;
        spawnTime: number;
    }[] = [];

    onActivate() {
        this._initializeParticleCache();
    }

    onDeactivate() {
        this._clearParticleCache();
    }

    // Initialize the particle cache
    private _initializeParticleCache(): void {
        if (!this.particlePrefab) {
            console.warn('Particle prefab not assigned');
            return;
        }

        for (let i = 0; i < this.cacheSize; i++) {
            const particle = this.particlePrefab.clone();
            particle.active = false;
            this._particleCache.push(particle);
        }
    }

    // Clear the particle cache
    private _clearParticleCache(): void {
        this._particleCache.forEach((particle) => particle.destroy());
        this._particleCache = [];
        this._particlesInUse = [];
    }

    // Spawn particles at a given position
    spawnParticles(position: vec3): void {
        for (let i = 0; i < this.spawnLimit; i++) {
            // Get a particle from the cache
            if (this._particleCache.length === 0) {
                console.warn('No particles available in cache!');
                return;
            }

            const particle = this._particleCache.pop()!;
            particle.active = true;
            particle.setPositionWorld(position);

            // Set a random velocity and rotation speed
            const velocity: vec3 = [
                (Math.random() - 0.5) * 10,
                Math.random() * 10,
                (Math.random() - 0.5) * 10,
            ];
            const rotationSpeed: vec3 = [
                (Math.random() - 0.5) * 360,
                (Math.random() - 0.5) * 360,
                (Math.random() - 0.5) * 360,
            ];

            this._particlesInUse.push({
                particle,
                velocity,
                rotationSpeed,
                spawnTime: performance.now() / 1000,
            });
        }
    }

    update(deltaTime: number): void {
        const currentTime = performance.now() / 1000;
        const particlesToRemove: number[] = [];

        this._particlesInUse.forEach((entry, index) => {
            const {particle, velocity, rotationSpeed, spawnTime} = entry;

            // Remove particle if lifetime has expired
            if (currentTime - spawnTime > this.particleLifetime) {
                particlesToRemove.push(index);
                return;
            }

            // Animate position and rotation
            this.animateParticle(particle, velocity, rotationSpeed, deltaTime);
        });

        // Return expired particles to the cache
        for (const index of particlesToRemove.reverse()) {
            const expiredParticle = this._particlesInUse[index];
            expiredParticle.particle.active = false;
            this._particleCache.push(expiredParticle.particle);
            this._particlesInUse.splice(index, 1);
        }
    }

    // Isolated particle animation logic
    private animateParticle(
        particle: Object3D,
        velocity: vec3,
        rotationSpeed: vec3,
        deltaTime: number
    ): void {
        switch (this.animationType) {
            case 0: // Firework animation
                this._fireworkAnimation(particle, velocity, rotationSpeed, deltaTime);
                break;
            case 1: // Fountain animation
                this._fountainAnimation(particle, velocity, rotationSpeed, deltaTime);
                break;
            default:
                console.warn('Unknown animation type!');
        }
    }

    // Firework animation logic
    private _fireworkAnimation(
        particle: Object3D,
        velocity: vec3,
        rotationSpeed: vec3,
        deltaTime: number
    ): void {
        const position = vec3.create();
        particle.getPositionWorld(position);

        vec3.scaleAndAdd(position, position, velocity, deltaTime);

        particle.setPositionWorld(position);

        // Apply gravity to Y-axis velocity
        velocity[1] -= 9.8 * deltaTime * 0.1;

        // Rotate the particle
        this._rotateParticle(particle, rotationSpeed, deltaTime);
    }

    // Fountain animation logic
    private _fountainAnimation(
        particle: Object3D,
        velocity: vec3,
        rotationSpeed: vec3,
        deltaTime: number
    ): void {
        const position = vec3.create();
        particle.getPositionWorld(position);

        const scaledVelocity = vec3.fromValues(
            velocity[0] * deltaTime * 0.5,
            velocity[1] * deltaTime,
            velocity[2] * deltaTime * 0.5
        );

        vec3.add(position, position, scaledVelocity);

        particle.setPositionWorld(position);

        velocity[1] -= 9.8 * deltaTime * 0.2;
        this._rotateParticle(particle, rotationSpeed, deltaTime);
    }

    // Rotate the particle
    private _rotateParticle(
        particle: Object3D,
        rotationSpeed: vec3,
        deltaTime: number
    ): void {
        const rotation = quat.create();
        particle.getRotationWorld(rotation);

        const f = (deltaTime * Math.PI) / 180;
        quat.rotateX(rotation, rotation, rotationSpeed[0] * f);
        quat.rotateY(rotation, rotation, rotationSpeed[1] * f);
        quat.rotateZ(rotation, rotation, rotationSpeed[2] * f);

        particle.setRotationWorld(rotation);
    }
}
